import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import ChatChannelsService from 'services/ServiceCHATCHANNELS';

const channelSchema = yup.object().shape({
  id: yup.string().required(),
  nome_canal: yup.string().required(),
  channel: yup.string().required(),
  ativado: yup.boolean().nullable(),
  label: yup.string().optional(),
  value: yup.string().optional()
});

const channelsListSchema = yup.array().of(channelSchema);

const useChatChannels = (props = {}) => {
  const { itemsPerPage = 100, showDisabled = false } = props;

  const [state, setState] = useState({
    response: [],
    isLoading: false,
    isError: false,
    error: null,
    totalRegisters: 0,
    activePage: 1,
    searchText: ''
  });

  const hasInitialFetch = useRef(false);
  const isMountedRef = useRef(true);

  const options = useMemo(
    () =>
      state.response.map(({ id, nome_canal, channel }) => ({
        label: `${nome_canal} (${channel})`,
        value: id
      })),
    [state.response]
  );

  const safeSetState = useCallback((updater) => {
    if (isMountedRef.current) {
      setState(updater);
    }
  }, []);

  const fetchData = useCallback(
    async (selectedPage, search) => {
      safeSetState((prevState) => ({
        ...prevState,
        isLoading: true,
        isError: false,
        error: null
      }));

      try {
        const response = await ChatChannelsService.getAll(
          selectedPage,
          itemsPerPage
        );

        if (!isMountedRef.current) return;

        const rawData = response?.data?.data || {};
        let dataToList = [];

        let array = [];
        let nomes = [
          'facebook',
          'watson',
          'whatsapp',
          'telegram',
          'instagram',
          'sms',
          'webchat',
          'flex4u',
          'chat'
        ];

        nomes.forEach((nome) => {
          if (!rawData[nome]) return;
          if (Array.isArray(rawData[nome])) {
            rawData[nome].forEach((item) => {
              if (item) array.push(item);
            });
          } else {
            if (rawData[nome]) array.push(rawData[nome]);
          }
        });

        dataToList = array.filter((item) => {
          if (!item) return false;

          if (search) {
            const lowerSearch = search.toLowerCase();
            const nameMatch = item.nome_canal
              ?.toLowerCase()
              .includes(lowerSearch);
            const channelMatch = item.channel
              ?.toLowerCase()
              .includes(lowerSearch);
            return nameMatch || channelMatch;
          }
          return true;
        });

        if (!showDisabled) {
          dataToList = dataToList.filter((item) => item.ativado);
        }

        const validation = channelsListSchema.safeValidateSync(dataToList, {
          abortEarly: false,
          stripUnknown: true
        });

        if (!validation.success) {
          throw {
            name: 'ValidationError',
            message: validation.error.message,
            errors: validation.error.errors
          };
        }

        const total = dataToList.length;

        if (!isMountedRef.current) return;

        safeSetState((prevState) => ({
          ...prevState,
          response: validation.data,
          totalRegisters: total,
          isLoading: false,
          activePage: selectedPage,
          searchText: search
        }));
      } catch (err) {
        if (!isMountedRef.current) return;

        console.error('Erro no fetch de canais de chat:', err);
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          'Erro ao carregar canais de chat.';

        toast.error(errorMessage);

        safeSetState((prevState) => ({
          ...prevState,
          response: [],
          isLoading: false,
          isError: true,
          error: err
        }));
      }
    },
    [itemsPerPage, safeSetState, showDisabled]
  );

  const fetch = useCallback(
    (page, search) => {
      fetchData(page, search);
    },
    [fetchData]
  );

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!hasInitialFetch.current) {
      hasInitialFetch.current = true;
      fetchData(1, '');
    }
  }, [fetchData]);

  return {
    ...state,
    options,
    fetch
  };
};

export default useChatChannels;
